import React, { useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { ArrowDownOutlined } from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { saveIntendedUrl } from '../../utils/persistentNavigation';
import { PAGE_SEO, SEO_DEFAULTS } from '../../utils/seoConfig';
import { trackEvent } from '../../utils/analytics';
import {
  generateAIEmployeesSchema,
  generateFAQSchema,
  generateProductTourVideoSchema,
  JsonLd,
} from '../../utils/structuredData';
import { useInMarketingShell } from '../Marketing/MarketingContext';
import HomeFold from '../Home/HomeFold';
import HomeVideoTile from '../Home/HomeVideoTile';
import HomeHiringStrip from '../Home/HomeHiringStrip';
import HomeLogoRow from '../Home/HomeLogoRow';
import { AIE_FOLD, AIE_TOUR_TILE } from './aieFoldContent';
import AIEStepsStrip from './sections/AIEStepsStrip';

import AIEWall from './sections/AIEWall';
import AIEControlSection from './sections/AIEControlSection';
import AIETeamSection from './sections/AIETeamSection';

import AIENoraFilm from './sections/AIENoraFilm';
import HomeTrustRow from '../Home/HomeTrustRow';
import AIEEconomics from './sections/AIEEconomics';
import AIEMoat from './sections/AIEMoat';
import AIEProofSection from './sections/AIEProofSection';
import AIERolesCompact from './roles/AIERolesCompact';
import AIEFaq, { AIE_FAQ_ITEMS } from './sections/AIEFaq';
import AIEClose from './sections/AIEClose';
import MobileStickyCta from '../Marketing/MobileStickyCta';
import './AIEmployeesLandingPage.css';

/**
 * AIEmployeesLandingPage — the public marketing page at cellcog.ai/ai-employees.
 *
 * REVAMPED Aug 5 2026 (mock-driven session with Nitish; 17 rulings),
 * RESTRUCTURED Sep 1 2026 (the sticky-split wall session, ~45 live rulings;
 * source mock lives in Maeve's workspace at aie_wall_mocks/w.html), and
 * FIRST SCREEN REPLACED Sep 9 2026 (Nitish's direction, at the desk): the
 * hero + autoplay product-tour video became HOME'S fold — the same layout
 * (H1, this page's own sub-line, benchmark pill, three trust bullets, the
 * INTERACTIVE DEMO FRAME) with "Get started" + a secondary "See how it works"
 * that scrolls to the wall; the tour film moved below the fold as home's
 * click-to-play tile. Rationale: traffic into this page is growing and the
 * demo frame is the best of the product (home read: demo touch 4.9→15% of
 * visitors after the fold shipped). A bet, not a fix, at ~14 visits/day.
 * Section order:
 *   1. Fold (HomeFold: H1 + sub + pill + bullets + CTAs + demo frame)
 *   2. Hiring strip (real recent hires)
 *   3. Product-tour film tile (HomeVideoTile, click-to-play lightbox)
 *   4. "Manage outcomes, not prompts." 3-step strip
 *   5. "What they can do" — the 9-pane sticky-split WALL (AIEWall), carrying
 *      the section's one persistent CTA (replaces the stacked blocks +
 *      mid-page CTA); id="aie-how" is the secondary CTA's scroll target
 *   6. Nora film (moved directly after the wall, Aug 31 ruling)
 *   7. "An owner, not an operator." merged control section (AIEControlSection,
 *      Sep 1 2026: replaced the two rails sections, six cards became three)
 *   8. "Hire one, or a team." (AIETeamSection, Sep 9 2026: one centred block,
 *      the org-chart claim in words, no diagram)
 *   9. Economics (no cost figure; two claims, B3 design Sep 2 2026)
 *  10. Moat (chatbot contrast + modality chips + pillar links)
 *  11. Proof (benchmark badge + sanctioned quotes)
 *  12. Roles (top-7 by GA4 views + expand-in-place)
 *  13. FAQ (10 merged items → FAQPage JSON-LD)
 *  14. Close + MobileStickyCta ("Get started" since Sep 9 2026, single clean button)
 *
 * ANALYTICS: every CTA fires cta_click {cta_name:'hire_ai_employee',
 * page:'ai-employees', location: hero|wall_sticky|economics|close|sticky_bar}.
 * The mid_page series ENDS at the Sep-1 wall deploy (new placement = new
 * label, event name unchanged). Sep 9 deploy seams: the autoplay video series
 * (video:'aie-hero', location:'hero' + hero_video_seek/restart/unmute +
 * watch_how_it_works) ENDS; new series begin — see_how_it_works (hero),
 * tour_video_tile_play + video_progress_* {video:'aie-tour', location:
 * 'video_tile'}, and the demo frame's demo_* events with compare_page
 * 'ai-employees'. section_view fires once per section via the page-level
 * observer (sections stay tracking-free — same pattern as SuperAgentsPage).
 * AIEHero + AIEHeroVideo are unmounted here (AIEHero has no other importer);
 * cleanup is a separate decision.
 */
const AIEmployeesLandingPage = () => {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const { currentUser } = useAuth();
  const inMarketingShell = useInMarketingShell();
  const dark = inMarketingShell ? true : isDarkMode;
  const rootRef = useRef(null);

  const ctaLabel = AIE_FOLD.cta;

  const handlePrimary = (location) => () => {
    trackEvent('cta_click', {
      cta_name: 'hire_ai_employee',
      page: 'ai-employees',
      location,
    });
    if (!currentUser) {
      saveIntendedUrl('/ai_employee');
      navigate('/signup');
      return;
    }
    navigate('/ai_employee');
  };

  // "See how it works" (Sep 9, replaced "Watch how it works"): the secondary
  // now scrolls to the wall — the section that IS how it works — with its
  // heading at the top of the screen (72px clears the sticky header). The
  // film has its own tile below the fold. NEW event name on purpose: the
  // behavior changed, so the watch_how_it_works series ends at this deploy.
  const handleSeeHow = () => {
    trackEvent('cta_click', { cta_name: 'see_how_it_works', page: 'ai-employees', location: 'hero' });
    const el = document.getElementById('aie-how');
    if (!el) return;
    // scrollIntoView, not window.scrollTo: the page scrolls the window in the
    // marketing shell but a nested container when mounted inside the app
    // (localhost with a signed-in session). The header clearance is the wall's
    // scroll-margin-top (AIEWall.css).
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // section_view: one event per top-level section, first time any part enters
  // the viewport (page-level observer; sections stay tracking-free).
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return undefined;
    const root = rootRef.current;
    if (!root) return undefined;
    const els = Array.from(root.querySelectorAll('[data-aie-section]'));
    const seen = new Set();
    const io = new IntersectionObserver((entries) => {
      entries.forEach((en) => {
        if (!en.isIntersecting) return;
        // Tall sections (feature_blocks ≈ 6 viewports on phones) can never
        // reach a 0.2 visibility ratio — count a section as viewed when EITHER
        // 20% of it OR half a viewport of it is on screen, whichever first.
        const vh = (en.rootBounds && en.rootBounds.height) || window.innerHeight;
        const enough = en.intersectionRatio >= 0.2
          || en.intersectionRect.height >= vh * 0.5;
        if (!enough) return;
        const label = en.target.getAttribute('data-aie-section');
        if (seen.has(label)) return;
        seen.add(label);
        trackEvent('section_view', { section: label, page: 'ai-employees' });
        io.unobserve(en.target);
      });
    }, { threshold: [0.05, 0.1, 0.15, 0.2] });
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  const seo = PAGE_SEO.aiEmployees;
  // Self-canonical at /ai-employees — the AI-Employee product deep-dive
  // (the homepage hub owns the brand/hire head term).
  const canonicalUrl = `${SEO_DEFAULTS.url}/ai-employees`;
  const ogImage = `${SEO_DEFAULTS.url}/og-image.png`;

  return (
    <>
      <Helmet>
        <title>{seo.title}</title>
        <meta name="description" content={seo.description} />
        <link rel="canonical" href={canonicalUrl} />
        {/* .md alternate: generated from this page's prerendered HTML at postbuild (scripts/marketingHtmlTwins.js, Oct 11 2026). */}
        <link rel="alternate" type="text/markdown" href={`${canonicalUrl}/index.md`} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={seo.title} />
        <meta property="og:description" content={seo.description} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:site_name" content={SEO_DEFAULTS.siteName} />
        <meta property="og:video" content={`${SEO_DEFAULTS.url}/assets/cellcog/product-tour-page.mp4`} />
        <meta property="og:video:type" content="video/mp4" />
        <meta property="og:video:width" content="1600" />
        <meta property="og:video:height" content="900" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seo.title} />
        <meta name="twitter:description" content={seo.description} />
        <meta name="twitter:image" content={ogImage} />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link
          href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Inter+Tight:wght@500;600;700;800&display=swap"
          rel="stylesheet"
        />
        {/* The Aug-9 poster preload (the hero video's poster was the LCP
            element) was removed Sep 9 with the hero video: the fold is now
            prerendered markup + the demo frame, and the poster only paints
            as the lazy tile thumbnail below the fold. */}
      </Helmet>
      {/* Product schema only — sitewide entity schema lives on the homepage hub. */}
      <JsonLd data={generateAIEmployeesSchema()} />
      <JsonLd data={generateFAQSchema(AIE_FAQ_ITEMS)} />
      {/* 60s product tour video — VideoObject for video rich results (Aug 5). */}
      <JsonLd data={generateProductTourVideoSchema()} />

      <div className={`aie-landing aie-page--employees ${dark ? 'dark' : 'light'}`} ref={rootRef}>
        {/* Home's fold on this page (Sep 9): this page's own sub-line, the
            demo frame as the proof, "Get started" + "See how it works". The
            band paints the near-black ground this page's hero always had. */}
        <div data-aie-section="hero">
          <HomeFold
            content={AIE_FOLD}
            badgePage="ai-employees"
            demoPage="ai-employees"
            band
            onPrimary={handlePrimary('hero')}
            secondary={{
              label: AIE_FOLD.secondary,
              icon: <ArrowDownOutlined />,
              onClick: handleSeeHow,
            }}
          />
        </div>

        {/* "Used by people who work at" (was "People use CellCog at") (Nitish, Sep 25 2026): same component as home,
            right under the demo. The hiring strip moved down to the Roles section. */}
        <div data-aie-section="logo_row"><HomeLogoRow /></div>

        {/* The product-tour film, one scroll beat after the demo frame — the
            same tile and position as home's hire film (Sep 9). */}
        <div data-aie-section="video_tile">
          <HomeVideoTile
            content={AIE_TOUR_TILE}
            page="ai-employees"
            video="aie-tour"
            playEvent="tour_video_tile_play"
          />
        </div>

        <div data-aie-section="steps"><AIEStepsStrip /></div>
        {/* Real recent hires, right after the steps (Nitish, Sep 25 2026 20:10 ET): one section below the logo row,
            so the two moving strips never stack. */}
        <div data-aie-section="hiring_strip"><HomeHiringStrip /></div>

        {/* Sticky-split wall (Sep 1 2026, replaces the nine stacked blocks +
            the mid-page CTA): nine panes scroll past ONE pinned frame; the
            wall carries its own persistent CTA under the frame — Nitish's
            ruling, the section's ONE action. cta_click location wall_sticky
            (the mid_page series ended at the wall deploy; the short-lived
            wall_mobile series ends at THIS deploy — s238, Nitish: on phones
            the page's own sticky bar carries the action, no in-pane CTA). */}
        <div data-aie-section="feature_blocks">
          <AIEWall
            ctaLabel={ctaLabel}
            onPrimarySticky={handlePrimary('wall_sticky')}
          />
        </div>

        {/* Nora film directly after the wall (Nitish, Aug 31): the proof
            beat lands right where the product story ends. */}
        <div data-aie-section="nora_film"><AIENoraFilm /></div>

        {/* Trust row (Nitish, Sep 16 2026): the same two tiles home carries after
            its film (CASA Tier 2 + Verified for Gmail), one shared component;
            claims per org canon `security-claims`. */}
        <div data-aie-section="trust_row"><HomeTrustRow page="ai_employees" /></div>

        {/* Merged ownership + control (Nitish, Sep 1 2026, C2 ruled from a
            rendered mock): the two rails sections ("True ownership" +
            "Control") became ONE statement-left / three-rows-right section.
            section_view series owner_rails + control_rails END at this deploy;
            the new label is `control`. AIERailsSection was deleted Sep 1 2026
            (Nitish's cleanup go).

            org_chart section REMOVED Aug 31 (Nitish, with the home mount): the
            example org read as heavy setup work to a first-time buyer; hirers
            succeed without ever building reporting lines. Deep org story keeps
            /ai-organization + /story. AIEOrgChart is now fully orphaned. */}
        <div data-aie-section="control"><AIEControlSection /></div>

        {/* Team claim in words (Nitish, Sep 9 2026, ruled from a rendered mock):
            one centred block, no cards, no chart. Restores the one thing the
            Aug-31 org-chart removal took off the page (a heading saying a team
            is possible) without the diagram. New section_view label `team`. */}
        <div data-aie-section="team"><AIETeamSection /></div>

        <div data-aie-section="economics">
          <AIEEconomics ctaLabel={ctaLabel} onPrimary={handlePrimary('economics')} />
        </div>

        <div data-aie-section="moat"><AIEMoat /></div>
        <div data-aie-section="proof"><AIEProofSection /></div>
        <div data-aie-section="roles"><AIERolesCompact /></div>
        <div data-aie-section="faq"><AIEFaq /></div>

        <div data-aie-section="close">
          <AIEClose ctaLabel={ctaLabel} onPrimary={handlePrimary('close')} demoPage="/ai-employees" />
        </div>

        {/* Sticky bar carries the page's ONE label (Nitish, Sep 9: "Get started"
            everywhere; the Sep-7 hire-cluster ruling reached the fold, this bar
            had kept the old words). Event name sticky_bar unchanged. */}
        <MobileStickyCta label={ctaLabel} onClick={handlePrimary('sticky_bar')} />
      </div>
    </>
  );
};

export default AIEmployeesLandingPage;
